import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { formatLocalDateString, parseLocalDate } from '@/lib/dateUtils';

// Module-level constants - never recreated
const MONTHS = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' }
];

// Generate years once at module load
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 11 }, (_, i) => {
  const year = CURRENT_YEAR - 5 + i;
  return { value: year.toString(), label: year.toString() };
});

// Default color for unknown leave types
const DEFAULT_LEAVE_COLOR = 'bg-gray-100 text-gray-800 border-gray-200';

// Convert hex color to Tailwind-like classes
function hexToTailwindClasses(hexColor) {
  if (!hexColor) return DEFAULT_LEAVE_COLOR;

  // Map common hex colors to Tailwind classes for better consistency
  const colorMap = {
    // Standard system colors
    '#8B5CF6': 'bg-violet-100 text-violet-800 border-violet-200',
    '#EF4444': 'bg-red-100 text-red-800 border-red-200',
    '#3B82F6': 'bg-blue-100 text-blue-800 border-blue-200',
    '#10B981': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    '#6B7280': 'bg-gray-100 text-gray-800 border-gray-200',
    '#F59E0B': 'bg-amber-100 text-amber-800 border-amber-200',
    '#9CA3AF': 'bg-gray-100 text-gray-600 border-gray-200',
    // Custom colors from database
    '#5FF7DD': 'bg-cyan-100 text-cyan-800 border-cyan-200',
    '#037720': 'bg-green-100 text-green-800 border-green-200',
    '#00FF33': 'bg-green-100 text-green-800 border-green-200',
    // Additional common colors
    '#EC4899': 'bg-pink-100 text-pink-800 border-pink-200',
    '#F97316': 'bg-orange-100 text-orange-800 border-orange-200',
    '#84CC16': 'bg-lime-100 text-lime-800 border-lime-200',
    '#06B6D4': 'bg-cyan-100 text-cyan-800 border-cyan-200',
    '#A855F7': 'bg-purple-100 text-purple-800 border-purple-200',
    '#14B8A6': 'bg-teal-100 text-teal-800 border-teal-200',
    '#6366F1': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  };

  // Check if we have a mapped color
  const upperHex = hexColor.toUpperCase();
  if (colorMap[upperHex]) {
    return colorMap[upperHex];
  }

  // For unknown hex colors, generate based on brightness
  const hex = upperHex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5
    ? 'bg-white border-2 text-gray-900'
    : 'bg-gray-100 border-2 text-gray-900';
}

// Generate abbreviation from leave type name
function generateAbbreviation(name) {
  if (!name) return 'L';

  // Common abbreviations
  const knownAbbr = {
    'Annual Leave': 'AL',
    'Sick Leave': 'SL',
    'Unpaid Leave': 'UL',
    'Work From Home': 'WFH',
    'Parental Leave': 'PL',
    'Personal Leave': 'PER',
    'Bereavement Leave': 'BL',
    'Study Leave': 'STU',
    'Birthday Leave': 'BD',
    'Christmas Shutdown': 'XMS',
    'Christmas Shut Down': 'XMS',
  };

  if (knownAbbr[name]) return knownAbbr[name];

  // Generate from first letters of words
  const words = name.split(' ').filter(w => w.length > 0);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words.map(w => w[0]).join('').toUpperCase().substring(0, 3);
}

/**
 * Hook to load team calendar data with single optimized RPC call
 * Replaces 5 sequential queries with 1 call + pre-indexed data
 *
 * @param {string} companyId - Company ID
 * @param {Date} currentDate - Current calendar date
 * @param {boolean} isAdmin - Whether user has admin permissions
 * @param {string} userId - Current user's profile ID (for leave type visibility)
 * @returns {Object} - Calendar data and helpers
 */
export function useTeamCalendar(companyId, currentDate, isAdmin = false, userId = null) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  /**
   * Load calendar data using the optimized RPC
   */
  const loadCalendarData = useCallback(async () => {
    if (!companyId || !currentDate) {
      console.log('[useTeamCalendar] Missing companyId or currentDate');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useTeamCalendar] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useTeamCalendar] Loading data with single RPC call...');
      console.time('[useTeamCalendar] RPC call duration');

      // Calculate month boundaries
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);

      // Format dates for PostgreSQL (use local date to avoid timezone shift)
      const monthStartStr = formatLocalDateString(monthStart);
      const monthEndStr = formatLocalDateString(monthEnd);

      // Single RPC call replaces 5 sequential queries!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_team_calendar_page',
        {
          p_company_id: companyId,
          p_month_start: monthStartStr,
          p_month_end: monthEndStr,
          p_year: year,
          p_is_admin: isAdmin
        }
      );

      console.timeEnd('[useTeamCalendar] RPC call duration');

      if (rpcError) {
        console.error('[useTeamCalendar] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useTeamCalendar] RPC response:', {
        requestsCount: rpcData?.requests?.length || 0,
        bankHolidaysCount: rpcData?.bank_holidays?.length || 0,
        calendarNotesCount: rpcData?.calendar_notes?.length || 0,
        leaveTypesCount: rpcData?.leave_types?.length || 0,
        todayRequestsCount: rpcData?.today_requests?.length || 0
      });

      // Convert bank_holidays_set to a Set for O(1) lookup
      const bankHolidaysSet = new Set(rpcData?.bank_holidays_set || []);

      // Create a Map for bank holiday names
      const bankHolidaysMap = new Map();
      (rpcData?.bank_holidays || []).forEach(h => {
        bankHolidaysMap.set(h.date, h.name);
      });

      // Create a Map for calendar notes by date
      const notesMap = new Map();
      (rpcData?.calendar_notes || []).forEach(n => {
        notesMap.set(n.note_date, n);
      });

      setData({
        ...rpcData,
        bankHolidaysSet,
        bankHolidaysMap,
        notesMap
      });
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useTeamCalendar] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId, currentDate, isAdmin]);

  // Auto-load when dependencies change
  useEffect(() => {
    loadCalendarData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadCalendarData]);

  /**
   * Get days array for the current month (memoized)
   */
  const daysInMonth = useMemo(() => {
    if (!currentDate) return [];

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysCount = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of month
    for (let day = 1; day <= daysCount; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [currentDate]);

  /**
   * Get requests for a specific date - O(1) lookup from pre-indexed data
   */
  const getRequestsForDate = useCallback((date) => {
    if (!date || !data?.requests_by_date) return [];
    const dateStr = formatLocalDateString(date);
    return data.requests_by_date[dateStr] || [];
  }, [data?.requests_by_date]);

  /**
   * Check if date is a bank holiday - O(1) lookup
   */
  const isBankHoliday = useCallback((date) => {
    if (!date || !data?.bankHolidaysSet) return false;
    const dateStr = formatLocalDateString(date);
    return data.bankHolidaysSet.has(dateStr);
  }, [data?.bankHolidaysSet]);

  /**
   * Get bank holiday name - O(1) lookup
   */
  const getBankHolidayName = useCallback((date) => {
    if (!date || !data?.bankHolidaysMap) return null;
    const dateStr = formatLocalDateString(date);
    return data.bankHolidaysMap.get(dateStr) || null;
  }, [data?.bankHolidaysMap]);

  /**
   * Get calendar note for date - O(1) lookup
   */
  const getNoteForDate = useCallback((date) => {
    if (!date || !data?.notesMap) return null;
    const dateStr = formatLocalDateString(date);
    return data.notesMap.get(dateStr) || null;
  }, [data?.notesMap]);

  /**
   * Find leave type config by name or ID
   */
  const findLeaveTypeConfig = useCallback((leaveType) => {
    const leaveTypes = data?.leave_types || [];
    return leaveTypes.find(lt =>
      lt.name === leaveType ||
      lt.id === leaveType ||
      lt.name.toLowerCase().replace(/\s+/g, '_') === leaveType
    );
  }, [data?.leave_types]);

  /**
   * Check if a leave type should be hidden from the current user
   */
  const shouldHideLeaveType = useCallback((leaveType, requestOwnerId) => {
    // If user is viewing their own leave, always show the full type
    if (requestOwnerId === userId) {
      return false;
    }

    // If user is admin/manager, always show the full type
    if (isAdmin) {
      return false;
    }

    // Find the leave type configuration
    const leaveTypeConfig = findLeaveTypeConfig(leaveType);

    // If leave type is admin-only, hide it from non-admins
    if (leaveTypeConfig?.required_permission === 'attendance.manage') {
      return true;
    }

    return false;
  }, [findLeaveTypeConfig, userId, isAdmin]);

  /**
   * Get leave type color - uses dynamic colors from database
   */
  const getLeaveTypeColor = useCallback((type, requestOwnerId) => {
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return DEFAULT_LEAVE_COLOR;
    }

    // Find the leave type config to get its color
    const leaveTypeConfig = findLeaveTypeConfig(type);

    if (leaveTypeConfig?.color) {
      return hexToTailwindClasses(leaveTypeConfig.color);
    }

    return DEFAULT_LEAVE_COLOR;
  }, [shouldHideLeaveType, findLeaveTypeConfig]);

  /**
   * Get raw hex color for inline styling
   */
  const getLeaveTypeHexColor = useCallback((type, requestOwnerId) => {
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return '#9CA3AF'; // Gray for hidden types
    }

    const leaveTypeConfig = findLeaveTypeConfig(type);
    return leaveTypeConfig?.color || '#9CA3AF';
  }, [shouldHideLeaveType, findLeaveTypeConfig]);

  /**
   * Get leave type display name - uses actual name from database
   */
  const getLeaveTypeName = useCallback((type, requestOwnerId) => {
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return 'Leave';
    }

    // The type IS the name in our system
    if (typeof type === 'string') {
      // Verify it exists in our leave types
      const leaveTypeConfig = findLeaveTypeConfig(type);
      if (leaveTypeConfig) {
        return leaveTypeConfig.name;
      }
      // If it's a string that looks like a name, use it
      if (type.includes(' ') || type[0] === type[0].toUpperCase()) {
        return type;
      }
    }

    return 'Leave';
  }, [shouldHideLeaveType, findLeaveTypeConfig]);

  /**
   * Get leave type abbreviation - generates from name
   */
  const getLeaveTypeAbbr = useCallback((type, requestOwnerId) => {
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return 'L';
    }

    // The type IS the name in our system
    if (typeof type === 'string') {
      return generateAbbreviation(type);
    }

    return 'L';
  }, [shouldHideLeaveType]);

  /**
   * Get day type display (Full Day, Half Day AM, Half Day PM)
   */
  const getDayTypeDisplay = useCallback((request, currentDate) => {
    if (!request.part_day) {
      return 'Full Day';
    }

    const dateStr = formatLocalDateString(currentDate);
    const startDate = request.start_date;
    const endDate = request.end_date;

    if (dateStr === startDate && request.part_day.start) {
      return request.part_day.start === 'AM' ? 'Half Day AM' : 'Half Day PM';
    }

    if (dateStr === endDate && request.part_day.end) {
      return request.part_day.end === 'AM' ? 'Half Day AM' : 'Half Day PM';
    }

    return 'Full Day';
  }, []);

  /**
   * Get day type abbreviation (AM, PM, or empty)
   */
  const getDayTypeAbbr = useCallback((request, currentDate) => {
    if (!request.part_day) {
      return '';
    }

    const dateStr = formatLocalDateString(currentDate);
    const startDate = request.start_date;
    const endDate = request.end_date;

    if (dateStr === startDate && request.part_day.start) {
      return request.part_day.start === 'AM' ? 'AM' : 'PM';
    }

    if (dateStr === endDate && request.part_day.end) {
      return request.part_day.end === 'AM' ? 'AM' : 'PM';
    }

    return '';
  }, []);

  /**
   * Format date range for display
   */
  const formatDateRange = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
    return `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  }, []);

  /**
   * Check if date is today
   */
  const isToday = useCallback((date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  /**
   * Check if date is weekend
   */
  const isWeekend = useCallback((date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    requests: data?.requests || [],
    bankHolidays: data?.bank_holidays || [],
    calendarNotes: data?.calendar_notes || [],
    leaveTypes: data?.leave_types || [],
    todayRequests: data?.today_requests || [],

    // Pre-computed
    daysInMonth,

    // Loading state
    loading,
    error,

    // Actions
    refetch: loadCalendarData,

    // O(1) lookup helpers
    getRequestsForDate,
    isBankHoliday,
    getBankHolidayName,
    getNoteForDate,

    // Leave type helpers
    findLeaveTypeConfig,
    shouldHideLeaveType,
    getLeaveTypeColor,
    getLeaveTypeHexColor,
    getLeaveTypeName,
    getLeaveTypeAbbr,
    getDayTypeDisplay,
    getDayTypeAbbr,
    formatDateRange,

    // Date helpers
    isToday,
    isWeekend,

    // Static data (module-level constants)
    months: MONTHS,
    years: YEARS
  };
}
